import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export interface UserPreferences {
  id: string;
  user_id: string;
  // Notification settings
  notify_new_papers: boolean;
  notify_comments: boolean;
  notify_mentions: boolean;
  notify_updates: boolean;
  notify_ai_review_ready: boolean;
  email_frequency: string;
  // Privacy settings
  profile_visibility: boolean;
  show_email: boolean;
  allow_mentions: boolean;
  allow_search_indexing: boolean;
  analytics_consent: boolean;
  // GDPR Art. 21 - Processing objections
  object_to_analytics: boolean;
  object_to_ai_processing: boolean;
  // GDPR Art. 18 - Account restriction
  account_restricted: boolean;
  restriction_reason: string | null;
  restriction_requested_at: string | null;
  // Timestamps
  created_at: string;
  updated_at: string;
}

const defaultPreferences: Omit<UserPreferences, 'id' | 'user_id' | 'created_at' | 'updated_at'> = {
  notify_new_papers: true,
  notify_comments: true,
  notify_mentions: true,
  notify_updates: false,
  notify_ai_review_ready: true,
  email_frequency: 'daily',
  profile_visibility: true,
  show_email: false,
  allow_mentions: true,
  allow_search_indexing: true,
  analytics_consent: true,
  object_to_analytics: false,
  object_to_ai_processing: false,
  account_restricted: false,
  restriction_reason: null,
  restriction_requested_at: null,
};

export function useUserPreferences() {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Fetch preferences
  const fetchPreferences = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Try to fetch existing preferences
      const { data, error } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        throw error;
      } else if (!data) {
        // No preferences found, create default
        const { data: newData, error: insertError } = await supabase
          .from('user_preferences')
          .insert({ user_id: user.id })
          .select()
          .single();

        if (insertError) throw insertError;
        setPreferences(newData as UserPreferences);
      } else {
        setPreferences(data as UserPreferences);
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
      toast.error('Failed to load preferences');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  // Update a single preference
  const updatePreference = useCallback(async <K extends keyof UserPreferences>(
    key: K,
    value: UserPreferences[K]
  ) => {
    if (!user?.id || !preferences) return false;

    try {
      setSaving(true);
      
      const { error } = await supabase
        .from('user_preferences')
        .update({ [key]: value })
        .eq('user_id', user.id);

      if (error) throw error;

      setPreferences(prev => prev ? { ...prev, [key]: value } : null);
      return true;
    } catch (error) {
      console.error('Error updating preference:', error);
      toast.error('Failed to save preference');
      return false;
    } finally {
      setSaving(false);
    }
  }, [user?.id, preferences]);

  // Update multiple preferences at once
  const updatePreferences = useCallback(async (
    updates: Partial<UserPreferences>
  ) => {
    if (!user?.id || !preferences) return false;

    try {
      setSaving(true);
      
      const { error } = await supabase
        .from('user_preferences')
        .update(updates)
        .eq('user_id', user.id);

      if (error) throw error;

      setPreferences(prev => prev ? { ...prev, ...updates } : null);
      toast.success('Settings saved successfully');
      return true;
    } catch (error) {
      console.error('Error updating preferences:', error);
      toast.error('Failed to save settings');
      return false;
    } finally {
      setSaving(false);
    }
  }, [user?.id, preferences]);

  // Restrict account (GDPR Art. 18)
  const restrictAccount = useCallback(async (reason: string) => {
    if (!user?.id) return false;

    try {
      setSaving(true);
      
      const { error } = await supabase
        .from('user_preferences')
        .update({
          account_restricted: true,
          profile_visibility: false,
          restriction_reason: reason,
          restriction_requested_at: new Date().toISOString(),
        })
        .eq('user_id', user.id);

      if (error) throw error;

      await supabase
        .from('papers')
        .update({ visibility: 'private' })
        .eq('author_id', user.id);

      setPreferences(prev => prev ? {
        ...prev,
        account_restricted: true,
        profile_visibility: false,
        restriction_reason: reason,
        restriction_requested_at: new Date().toISOString(),
      } : null);

      toast.success('Account restricted successfully');
      return true;
    } catch (error) {
      console.error('Error restricting account:', error);
      toast.error('Failed to restrict account');
      return false;
    } finally {
      setSaving(false);
    }
  }, [user?.id]);

  const unrestrictAccount = useCallback(async () => {
    if (!user?.id) return false;

    try {
      setSaving(true);
      
      const { error } = await supabase
        .from('user_preferences')
        .update({
          account_restricted: false,
          restriction_reason: null,
          restriction_requested_at: null,
        })
        .eq('user_id', user.id);

      if (error) throw error;

      setPreferences(prev => prev ? {
        ...prev,
        account_restricted: false,
        restriction_reason: null,
        restriction_requested_at: null,
      } : null);

      toast.success('Account restrictions removed');
      return true;
    } catch (error) {
      console.error('Error unrestricting account:', error);
      toast.error('Failed to remove restrictions');
      return false;
    } finally {
      setSaving(false);
    }
  }, [user?.id]);

  return {
    preferences,
    loading,
    saving,
    updatePreference,
    updatePreferences,
    restrictAccount,
    unrestrictAccount,
    refetch: fetchPreferences,
  };
}
